import { useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Printer, X } from 'lucide-react'
import { useCustodyDocument } from '../../../hooks/use-custody-actions'
import type { CustodyDocument } from '../../../types/custody-actions'

interface DestructionCertificateViewProps {
  caseId: string
  actionId: string
  open: boolean
  onClose: () => void
}

async function exportToPdf(element: HTMLElement, filename: string): Promise<void> {
  const mod = await import('html2pdf.js')
  const html2pdf = mod.default as () => {
    set: (opts: Record<string, unknown>) => {
      from: (el: HTMLElement) => { save: () => Promise<void> }
    }
  }
  await html2pdf()
    .set({
      margin: 10,
      filename,
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    })
    .from(element)
    .save()
}

interface CertMetaTopProps {
  caseReference: string
  caseName: string
  documentReference: string
  instructionDate: string | null
  complianceDate: string | null
}

function CertMetaTop({
  caseReference,
  caseName,
  documentReference,
  instructionDate,
  complianceDate,
}: CertMetaTopProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <>
      <div>
        <p className="text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
          {t('custodyActions.caseRef')}
        </p>
        <p className="font-medium text-[var(--text-primary)]">
          {caseReference} — {caseName}
        </p>
      </div>
      <div>
        <p className="text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
          {t('custodyActions.documentRef')}
        </p>
        <p className="font-[var(--font-mono)] text-[var(--text-primary)]">{documentReference}</p>
      </div>
      <div>
        <p className="text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
          {t('custodyActions.instructionDate')}
        </p>
        <p className="text-[var(--text-primary)]">{instructionDate ?? '—'}</p>
      </div>
      <div>
        <p className="text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
          {t('custodyActions.complianceDate')}
        </p>
        <p className="text-[var(--text-primary)]">{complianceDate ?? '—'}</p>
      </div>
    </>
  )
}

interface CertMetaBottomProps {
  authorisingParty: string | null
  executingParty: string | null
  destructionMethod: string | null
  generatedByName: string | null
  createdAt: string
}

function CertMetaBottom({
  authorisingParty,
  executingParty,
  destructionMethod,
  generatedByName,
  createdAt,
}: CertMetaBottomProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <>
      <div>
        <p className="text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
          {t('custodyActions.authorisingParty')}
        </p>
        <p className="text-[var(--text-primary)]">{authorisingParty ?? '—'}</p>
      </div>
      <div>
        <p className="text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
          {t('custodyActions.executingParty')}
        </p>
        <p className="text-[var(--text-primary)]">{executingParty ?? '—'}</p>
      </div>
      <div>
        <p className="text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
          {t('custodyActions.destructionMethod')}
        </p>
        <p className="text-[var(--text-primary)]">{destructionMethod ?? '—'}</p>
      </div>
      <div>
        <p className="text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
          {t('custodyActions.generatedBy')}
        </p>
        <p className="text-[var(--text-primary)]">{generatedByName ?? '—'}</p>
      </div>
      <div>
        <p className="text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
          {t('custodyActions.date')}
        </p>
        <p className="text-[var(--text-primary)]">{new Date(createdAt).toLocaleString()}</p>
      </div>
    </>
  )
}

interface CertContentProps {
  doc: CustodyDocument
}

function CertEvidenceList({ evidenceNumbers }: { evidenceNumbers: string[] }): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <div>
      <p className="mb-1 text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
        {t('custodyActions.evidenceItems')}
      </p>
      <ul className="space-y-1">
        {evidenceNumbers.map((num) => (
          <li key={num} className="text-xs font-[var(--font-mono)] text-[var(--text-primary)]">
            {num}
          </li>
        ))}
      </ul>
    </div>
  )
}

function nullStr(value: string | null | undefined): string | null {
  return value ?? null
}

function extractTopProps(doc: CustodyDocument): CertMetaTopProps {
  return {
    caseReference: doc.caseReference,
    caseName: doc.caseName,
    documentReference: doc.documentReference,
    instructionDate: nullStr(doc.action?.instructionDate),
    complianceDate: nullStr(doc.action?.complianceDate),
  }
}

function extractBottomProps(doc: CustodyDocument): CertMetaBottomProps {
  return {
    authorisingParty: nullStr(doc.action?.authorisingParty),
    executingParty: nullStr(doc.action?.executingParty),
    destructionMethod: nullStr(doc.action?.destructionMethod),
    generatedByName: doc.generatedByName,
    createdAt: doc.createdAt,
  }
}

function CertNotesSection({ notes }: { notes: string | null }): React.ReactElement | null {
  const { t } = useTranslation('evidence')
  if (notes === null || notes.length === 0) return null
  return (
    <div>
      <p className="text-xs font-[var(--font-mono)] text-[var(--text-muted)] uppercase">
        {t('custodyActions.notes')}
      </p>
      <p className="text-[var(--text-primary)]">{notes}</p>
    </div>
  )
}

function CertContent({ doc }: CertContentProps): React.ReactElement {
  const { t } = useTranslation('evidence')
  const topProps = extractTopProps(doc)
  const bottomProps = extractBottomProps(doc)
  const notes = nullStr(doc.action?.notes)

  return (
    <div className="space-y-4 text-sm font-[var(--font-body)]">
      <div className="grid grid-cols-2 gap-4">
        <CertMetaTop {...topProps} />
        <CertMetaBottom {...bottomProps} />
      </div>
      <CertEvidenceList evidenceNumbers={doc.evidenceNumbers} />
      <CertNotesSection notes={notes} />
      <p className="text-xs text-[var(--text-muted)]">{t('custodyActions.evidenceDestroyed')}</p>
    </div>
  )
}

interface DialogBodyProps {
  loading: boolean
  error: string | null
  doc: CustodyDocument | null
}

function DialogBody({ loading, error, doc }: DialogBodyProps): React.ReactElement {
  const { t } = useTranslation('evidence')
  if (loading)
    return (
      <div
        role="status"
        aria-busy="true"
        className="py-8 text-center text-sm text-[var(--text-muted)]"
      >
        {t('loading')}
      </div>
    )
  if (error)
    return (
      <div role="alert" className="py-4 text-sm text-red-600 dark:text-red-400">
        {t('custodyActions.errorFetchDocument')}
      </div>
    )
  if (doc === null) return <div className="py-4 text-sm text-[var(--text-muted)]" />
  return <CertContent doc={doc} />
}

interface DialogHeaderProps {
  title: string
  onExportPdf: () => void
  onPrint: () => void
  onClose: () => void
}

function DialogHeader({
  title,
  onExportPdf,
  onPrint,
  onClose,
}: DialogHeaderProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4">
      <h2 className="text-lg font-[var(--font-heading)] font-semibold text-[var(--text-primary)]">
        {title}
      </h2>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onExportPdf}
          className="rounded border border-[var(--border)] px-3 py-1.5 text-xs font-[var(--font-mono)] tracking-wider text-[var(--text-primary)] uppercase hover:opacity-80"
        >
          {t('custodyActions.exportPdf')}
        </button>
        <button
          type="button"
          onClick={onPrint}
          aria-label={t('custodyActions.print')}
          className="flex items-center gap-1 rounded border border-[var(--border)] px-3 py-1.5 text-xs font-[var(--font-mono)] tracking-wider text-[var(--text-primary)] uppercase hover:opacity-80"
        >
          <Printer className="h-3 w-3" />
          {t('custodyActions.print')}
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

export function DestructionCertificateView({
  caseId,
  actionId,
  open,
  onClose,
}: DestructionCertificateViewProps): React.ReactElement | null {
  const { t } = useTranslation('evidence')
  const printRef = useRef<HTMLDivElement>(null)
  const { document: doc, loading, error } = useCustodyDocument(caseId, actionId)

  const handlePrint = useCallback(() => {
    globalThis.print()
  }, [])
  const handleExportPdf = useCallback(() => {
    if (printRef.current === null) return
    void exportToPdf(printRef.current, `destruction-certificate-${actionId}.pdf`)
  }, [actionId])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg border border-[var(--border)] bg-[var(--bg-card)] shadow-xl">
        <DialogHeader
          title={t('custodyActions.destructionCertificate')}
          onExportPdf={handleExportPdf}
          onPrint={handlePrint}
          onClose={onClose}
        />
        <div className="overflow-y-auto p-6" ref={printRef}>
          <DialogBody loading={loading} error={error} doc={doc} />
        </div>
      </div>
    </div>
  )
}
